import { useState, useEffect } from 'react';
import api from '../services/api';
import Navigation from '../components/Navigation';
import {
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiEye,
  FiRefreshCw,
} from 'react-icons/fi';

const AdminDashboard = () => {
  const [contractors, setContractors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'submitted_at',
    sortOrder: 'DESC',
    limit: 50,
    offset: 0,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contractorsData, statsData] = await Promise.all([
        api.getContractors(filters),
        api.getAdminStats(),
      ]);
      setContractors(contractorsData.contractors || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contractorId, newStatus) => {
    try {
      await api.updateContractorStatus(contractorId, newStatus);
      await loadData();
      if (selectedContractor?.id === contractorId) {
        setSelectedContractor({ ...selectedContractor, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const viewContractor = async (id) => {
    try {
      const data = await api.getContractorById(id);
      setSelectedContractor(data.contractor);
    } catch (error) {
      console.error('Failed to load contractor:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: (
        <span className="px-3 py-1 bg-slate-500/20 text-slate-300 rounded-full text-sm font-medium">
          Draft
        </span>
      ),
      pending: (
        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiClock className="w-4 h-4" />
          Pending
        </span>
      ),
      approved: (
        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiCheckCircle className="w-4 h-4" />
          Approved
        </span>
      ),
      rejected: (
        <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm font-medium flex items-center gap-1">
          <FiXCircle className="w-4 h-4" />
          Rejected
        </span>
      ),
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navigation />
      <div className="container mx-auto px-4 py-12 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-slate-300">Manage contractor onboarding applications</p>
            </div>
            <button
              onClick={loadData}
              className="mt-4 md:mt-0 px-4 py-2 glass-button text-white font-semibold rounded-lg hover:bg-white/30 flex items-center gap-2"
            >
              <FiRefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-white mb-1">
                  {stats.contractors?.total || 0}
                </div>
                <div className="text-slate-300 text-sm">Total Contractors</div>
              </div>
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-yellow-400 mb-1">
                  {stats.contractors?.byStatus?.pending || 0}
                </div>
                <div className="text-slate-300 text-sm">Pending Review</div>
              </div>
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-green-400 mb-1">
                  {stats.contractors?.byStatus?.approved || 0}
                </div>
                <div className="text-slate-300 text-sm">Approved</div>
              </div>
              <div className="glass-card p-6">
                <div className="text-3xl font-bold text-blue-400 mb-1">
                  {stats.contractors?.recentSubmissions || 0}
                </div>
                <div className="text-slate-300 text-sm">Recent Submissions</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="glass-card p-6 mb-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value, offset: 0 })}
                    placeholder="Search by name, email, ID..."
                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value, offset: 0 })
                  }
                  className="form-select"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Sort By</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                  className="form-select"
                >
                  <option value="submitted_at">Submitted Date</option>
                  <option value="created_at">Created Date</option>
                  <option value="full_name">Name</option>
                  <option value="status">Status</option>
                  <option value="application_id">Application ID</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Order</label>
                <button
                  onClick={() =>
                    setFilters({
                      ...filters,
                      sortOrder: filters.sortOrder === 'ASC' ? 'DESC' : 'ASC',
                    })
                  }
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 flex items-center justify-between"
                >
                  <span>{filters.sortOrder === 'ASC' ? 'Ascending' : 'Descending'}</span>
                  {filters.sortOrder === 'ASC' ? (
                    <FiChevronUp className="w-5 h-5" />
                  ) : (
                    <FiChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Contractors Table */}
          <div className="glass-card">
            {loading ? (
              <div className="p-12 text-center text-white">Loading...</div>
            ) : contractors.length === 0 ? (
              <div className="p-12 text-center text-slate-300">No contractors found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Application ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Trades
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {contractors.map((contractor) => (
                      <tr key={contractor.id} className="hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-white">
                            {contractor.application_id || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {contractor.full_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">
                            {contractor.user_email || contractor.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-300">
                            {contractor.trades?.map((t) => t.name).join(', ') || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(contractor.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">
                            {contractor.submitted_at
                              ? new Date(contractor.submitted_at).toLocaleDateString()
                              : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => viewContractor(contractor.id)}
                            className="px-3 py-1 glass-button text-white text-sm font-semibold rounded-lg hover:bg-white/30 flex items-center gap-1"
                          >
                            <FiEye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Extra spacing at bottom to ensure content is visible */}
          <div className="h-8"></div>

          {/* Contractor Detail Modal - full submission review */}
          {selectedContractor && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="glass-card p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6 sticky top-0 bg-inherit pb-4 border-b border-white/10">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {selectedContractor.full_name || 'Contractor Details'}
                    </h2>
                    <p className="text-slate-400 text-sm">
                      {selectedContractor.application_id || 'No Application ID'} · Submitted {selectedContractor.submitted_at ? new Date(selectedContractor.submitted_at).toLocaleString() : '—'}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedContractor(null)}
                    className="text-slate-400 hover:text-white p-1"
                    aria-label="Close"
                  >
                    <FiXCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6 mt-6">
                  {/* Personal */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Personal Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">Full name:</span><span className="text-white ml-2">{selectedContractor.full_name || '—'}</span></div>
                      <div><span className="text-slate-400">Email:</span><span className="text-white ml-2">{selectedContractor.user_email || selectedContractor.email || '—'}</span></div>
                      <div><span className="text-slate-400">Telephone:</span><span className="text-white ml-2">{selectedContractor.telephone || '—'}</span></div>
                      <div className="md:col-span-2"><span className="text-slate-400">Address:</span><span className="text-white ml-2">{selectedContractor.address || '—'}</span></div>
                    </div>
                  </section>

                  {/* Business */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Business Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">Business name:</span><span className="text-white ml-2">{selectedContractor.business_name || '—'}</span></div>
                      <div><span className="text-slate-400">Business type:</span><span className="text-white ml-2">{selectedContractor.business_type || '—'}</span></div>
                      <div className="md:col-span-2"><span className="text-slate-400">Business address:</span><span className="text-white ml-2">{selectedContractor.business_address || '—'}</span></div>
                      <div><span className="text-slate-400">Trades:</span><span className="text-white ml-2">{selectedContractor.trades?.map((t) => t.name).join(', ') || '—'}</span></div>
                    </div>
                  </section>

                  {/* Registration */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Registration Info</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">CIS registration:</span><span className="text-white ml-2">{selectedContractor.cis_registration || '—'}</span></div>
                      <div><span className="text-slate-400">UTR:</span><span className="text-white ml-2">{selectedContractor.utr || '—'}</span></div>
                      <div><span className="text-slate-400">National insurance:</span><span className="text-white ml-2">{selectedContractor.national_insurance || '—'}</span></div>
                    </div>
                  </section>

                  {/* Experience */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Experience</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-slate-400">Time served:</span><span className="text-white ml-2">{selectedContractor.time_served || '—'}</span></div>
                      <div><span className="text-slate-400">Trade qualifications:</span><span className="text-white ml-2">{selectedContractor.trade_qualifications || '—'}</span></div>
                      <div><span className="text-slate-400">Trades competency:</span><span className="text-white ml-2">{selectedContractor.trades_competency || '—'}</span></div>
                    </div>
                  </section>

                  {/* References */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">References</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-slate-400">References:</span><span className="text-white ml-2">{selectedContractor.references_text || '—'}</span></div>
                      <div><span className="text-slate-400">Proof of work:</span><span className="text-white ml-2">{selectedContractor.proof_of_work || '—'}</span></div>
                      <div><span className="text-slate-400">Socials:</span><span className="text-white ml-2">{selectedContractor.socials || '—'}</span></div>
                    </div>
                  </section>

                  {/* Scope of Works */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Scope of Works</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">Preferred project type:</span><span className="text-white ml-2">{selectedContractor.preferred_project_type || '—'}</span></div>
                      <div><span className="text-slate-400">Max project value:</span><span className="text-white ml-2">{selectedContractor.max_project_value ?? '—'}</span></div>
                      <div><span className="text-slate-400">Pay type:</span><span className="text-white ml-2">{selectedContractor.pricing_preference === 'day_rate' ? 'Day rate' : 'Price'}</span></div>
                      {selectedContractor.pricing_preference === 'day_rate' && (
                        <div><span className="text-slate-400">Day rate:</span><span className="text-white ml-2">{selectedContractor.scope_day_rate ?? '—'}</span></div>
                      )}
                    </div>
                  </section>

                  {/* CSCS */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">CSCS Registration</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">CSCS registration:</span><span className="text-white ml-2">{selectedContractor.cscs_registration || '—'}</span></div>
                      <div><span className="text-slate-400">CSCS card number:</span><span className="text-white ml-2">{selectedContractor.cscs_card_number || '—'}</span></div>
                    </div>
                  </section>

                  {/* Payment */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Payment Info</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">Bank name:</span><span className="text-white ml-2">{selectedContractor.bank_name || '—'}</span></div>
                      <div><span className="text-slate-400">Sort code:</span><span className="text-white ml-2">{selectedContractor.sort_code || '—'}</span></div>
                      <div><span className="text-slate-400">Account number:</span><span className="text-white ml-2">{selectedContractor.account_number || '—'}</span></div>
                      <div><span className="text-slate-400">Name on account:</span><span className="text-white ml-2">{selectedContractor.name_on_account || '—'}</span></div>
                      <div><span className="text-slate-400">Day rate:</span><span className="text-white ml-2">{selectedContractor.day_rate ?? '—'}</span></div>
                      <div><span className="text-slate-400">Min job value:</span><span className="text-white ml-2">{selectedContractor.minimum_job_value ?? '—'}</span></div>
                      <div><span className="text-slate-400">Max job value:</span><span className="text-white ml-2">{selectedContractor.maximum_job_value ?? '—'}</span></div>
                    </div>
                  </section>

                  {/* Insurances & Qualifying Bodies */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Insurances & Qualifying Bodies</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-slate-400">Insurances:</span>
                        {Array.isArray(selectedContractor.insurances_file_path) && selectedContractor.insurances_file_path.length > 0 ? (
                          <ul className="mt-1 space-y-1">
                            {selectedContractor.insurances_file_path.map((filename) => (
                              <li key={filename}>
                                <button
                                  type="button"
                                  className="text-blue-400 hover:underline text-left bg-transparent border-none p-0 cursor-pointer font-inherit text-sm"
                                  onClick={() => window.open(api.getUploadUrl(filename), '_blank', 'noopener,noreferrer')}
                                >
                                  {filename}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-white ml-2">None uploaded</span>
                        )}
                      </div>
                      <div>
                        <span className="text-slate-400">Qualifying bodies:</span>
                        {Array.isArray(selectedContractor.qualifying_bodies_file_path) && selectedContractor.qualifying_bodies_file_path.length > 0 ? (
                          <ul className="mt-1 space-y-1">
                            {selectedContractor.qualifying_bodies_file_path.map((filename) => (
                              <li key={filename}>
                                <button
                                  type="button"
                                  className="text-blue-400 hover:underline text-left bg-transparent border-none p-0 cursor-pointer font-inherit text-sm"
                                  onClick={() => window.open(api.getUploadUrl(filename), '_blank', 'noopener,noreferrer')}
                                >
                                  {filename}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-white ml-2">None uploaded</span>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Clothing */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Clothing Sizes</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-slate-400">Waist:</span><span className="text-white ml-2">{selectedContractor.waist_size || '—'}</span></div>
                      <div><span className="text-slate-400">Jumper:</span><span className="text-white ml-2">{selectedContractor.jumper_size || '—'}</span></div>
                      <div><span className="text-slate-400">T-shirt:</span><span className="text-white ml-2">{selectedContractor.tshirt_size || '—'}</span></div>
                      <div><span className="text-slate-400">Gillet:</span><span className="text-white ml-2">{selectedContractor.gillet_size || '—'}</span></div>
                    </div>
                  </section>

                  {/* Drink */}
                  <section>
                    <h3 className="text-lg font-semibold text-white mb-3">Drink preferences</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-slate-400">Preferred drink:</span><span className="text-white ml-2">{selectedContractor.preferred_drink || '—'}</span></div>
                      <div><span className="text-slate-400">How they like it:</span><span className="text-white ml-2">{selectedContractor.drink_notes || '—'}</span></div>
                    </div>
                  </section>

                  {/* Update Status */}
                  <section className="pt-4 border-t border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">Update Status</h3>
                    <div className="flex gap-3">
                      {['pending', 'approved', 'rejected'].map((status) => (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(selectedContractor.id, status)}
                          disabled={selectedContractor.status === status}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                            status === 'approved'
                              ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                              : status === 'rejected'
                              ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                              : 'bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                  </section>

                  <div className="text-sm text-slate-500 pt-2">
                    Created: {new Date(selectedContractor.created_at).toLocaleString()}
                    {selectedContractor.submitted_at && (
                      <> · Submitted: {new Date(selectedContractor.submitted_at).toLocaleString()}</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
